import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ClientsManagement = () => {
  const { token, partner } = usePartnerAuth();
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', company: '' });
  const [creating, setCreating] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND}/api/partner/clients`, { headers: { Authorization: `Bearer ${token}` } });
      // Deduplicate clients by ID to avoid showing duplicates
      const uniqueClients = Array.from(new Map((res.data.data || []).map(c => [c.id, c])).values());
      setClients(uniqueClients);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { if (token) fetchClients(); }, [token, fetchClients]);

  const createClient = async (e) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    try {
      const payload = { ...form };
      const response = await axios.post(`${BACKEND}/api/partner/clients`, payload, { headers: { Authorization: `Bearer ${token}` } });
      console.log('Client creation response:', response);
      const createdClient = response?.data?.data;
      if (!createdClient) {
        console.error('Invalid response structure:', response?.data);
        throw new Error('Server returned invalid response');
      }
      setClients((prevClients) => [createdClient, ...prevClients]);
      setForm({ full_name: '', email: '', phone: '', company: '' });
      toast.success('Client added successfully');
      // Refresh the list in background without affecting success
      fetchClients().catch((refreshErr) => {
        console.error('Failed to refresh clients after add:', refreshErr);
      });
    } catch (err) {
      console.error('Create client error:', err);
      // Only show error if it's not a network error that still created the client
      if (err.response) {
        // Server responded with error status
        const message = err.response?.data?.detail || 'Failed to add client';
        toast.error(message);
      } else if (err.request) {
        // Network error
        console.warn('Network error during client creation, but client may have been created');
        toast.success('Client may have been added - please refresh the page to confirm');
      } else {
        // Other error
        toast.error('Failed to add client');
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Clients</h2>
      <Toaster position="top-right" richColors />
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-1 p-4 bg-white rounded shadow">
          <h3 className="font-semibold mb-2">Add Client</h3>
          <form onSubmit={createClient} className="space-y-2">
            <input value={form.full_name} onChange={e=>setForm({...form, full_name: e.target.value})} placeholder="Full name" className="w-full p-2 border rounded" />
            <input value={form.email} onChange={e=>setForm({...form, email: e.target.value})} placeholder="Email" className="w-full p-2 border rounded" />
            <input value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} placeholder="Phone" className="w-full p-2 border rounded" />
            <input value={form.company} onChange={e=>setForm({...form, company: e.target.value})} placeholder="Company" className="w-full p-2 border rounded" />
            <button className="w-full bg-orange-500 text-white py-2 rounded" disabled={creating}>
              {creating ? 'Creating...' : 'Create'}
            </button>
          </form>
        </div>

        <div className="md:col-span-2 p-4 bg-white rounded shadow">
          <h3 className="font-semibold mb-2">Client List</h3>
          <div className="space-y-2">
            {clients.map(c => (
              <div key={c.id} className="p-2 border rounded flex justify-between items-center">
                <div>
                  <div className="font-medium">{c.full_name}</div>
                  <div className="text-sm text-gray-500">{c.email} • {c.phone}</div>
                </div>
                <div>
                  <a href={`/partner/clients/${c.id}`} className="text-blue-600">Manage</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientsManagement;
